import { StatusFilters } from '@constants'

import {
  AccountingAdvancedExportedStatusMessage,
  AccountingExpressExportedStatusMessage,
  AccountingProcessedStatusMessage,
  AccountingTodoDoneStatusMessage,
  AccountingUnprocessedStatusMessage,
  ApprovedStatusMessage,
  ExcludedFromAccountingStatusMessage,
  FiledStatusMessage,
  HasAccountingIdStatusMessage,
  HasAccountingTodoStatusMessage,
  HasArtifactStatusMessage,
  HasAttachmentMessage,
  HasPostItMessage,
  HasUnfinishedAccountingTodoStatusMessage,
  HasVatPeriodStatusMessage,
  NewStatusMessage,
  NoAccountingIdStatusMessage,
  NoAccountingTodoStatusMessage,
  NoArtifactStatusMessage,
  NoAttachmentMessage,
  NoPostItMessage,
  NotApprovedStatusMessage,
  NotFiledStatusMessage,
  NoVatPeriodStatusMessage,
  ReaydToSyncStatusMessage,
  UncategorizedStatusMessage,
  UpdatedStatusMessage,
} from '@messages'

export function getExpenseStatusFilterConfig({
  hasIntegrationSzamlazz,
  isAccountantUser,
  isAdvancedAccounting,
}: Record<'hasIntegrationSzamlazz' | 'isAccountantUser' | 'isAdvancedAccounting', boolean>): StatusFilterConfig[] {
  if (isAccountantUser && isAdvancedAccounting) {
    return [
      {
        name: StatusFilters.IS_NEW,
        options: [{ value: true, label: NewStatusMessage }],
      },
      {
        name: StatusFilters.IS_FILED,
        options: [{ value: true, label: FiledStatusMessage }],
      },
      {
        name: StatusFilters.HAS_ARTIFACT,
        options: [{ value: false, label: NoArtifactStatusMessage }],
      },
      {
        name: StatusFilters.IS_PROCESSED,
        onChangeOverWriteRules: {
          [StatusFilters.IS_ACCOUNTING_EXPORTED]: undefined,
        },
        options: [
          { value: false, label: AccountingUnprocessedStatusMessage },
          { value: true, label: AccountingProcessedStatusMessage },
        ],
      },
      {
        name: StatusFilters.IS_ACCOUNTING_EXPORTED,
        onChangeOverWriteRules: { [StatusFilters.IS_PROCESSED]: undefined },
        options: [{ value: true, label: AccountingAdvancedExportedStatusMessage }],
      },
      {
        name: StatusFilters.HAS_UNFINISHED_ACCOUNTING_TODO,
        options: [{ value: true, label: HasUnfinishedAccountingTodoStatusMessage }],
      },
    ]
  }

  const config: StatusFilterConfig[] = [
    {
      name: StatusFilters.IS_NEW,
      options: [{ value: true, label: NewStatusMessage }],
    },
    {
      name: StatusFilters.IS_UPDATED,
      options: [{ value: true, label: UpdatedStatusMessage }],
    },
    {
      name: StatusFilters.HAS_ARTIFACT,
      options: [{ value: false, label: NoArtifactStatusMessage }],
    },

    {
      name: StatusFilters.IS_APPROVED,
      options: [{ value: false, label: NotApprovedStatusMessage }],
    },
    {
      name: StatusFilters.HAS_EXPENSE_TYPE,
      options: [{ value: false, label: UncategorizedStatusMessage }],
    },
  ]

  if (hasIntegrationSzamlazz) {
    config.push({
      name: StatusFilters.IS_READY_TO_SYNC_SZAMLAZZ,
      options: [{ value: true, label: ReaydToSyncStatusMessage }],
    })
  }

  return config
}

export function getExpenseStatusFilterDialogConfig({
  hasIntegrationSzamlazz,
  isAccountingIdEnabled,
  isAdvancedAccounting,
  isVatPeriodEnabled,
}: Record<
  'hasIntegrationSzamlazz' | 'isAccountingIdEnabled' | 'isAdvancedAccounting' | 'isVatPeriodEnabled',
  boolean
>): StatusFilterConfig[] {
  const approveConfig = [
    {
      name: StatusFilters.IS_APPROVED,
      options: [
        { value: true, label: ApprovedStatusMessage },
        { value: false, label: NotApprovedStatusMessage },
      ],
    },
  ]

  if (isAdvancedAccounting) {
    const config: StatusFilterConfig[] = [
      {
        name: StatusFilters.IS_NEW,
        options: [{ value: true, label: NewStatusMessage }],
      },
      {
        name: StatusFilters.IS_UPDATED,
        options: [{ value: true, label: UpdatedStatusMessage }],
      },
      {
        name: StatusFilters.HAS_ARTIFACT,
        options: [
          { value: true, label: HasArtifactStatusMessage },
          { value: false, label: NoArtifactStatusMessage },
        ],
      },
      {
        name: StatusFilters.HAS_POST_IT,
        options: [
          { value: true, label: HasPostItMessage },
          { value: false, label: NoPostItMessage },
        ],
      },
      {
        name: StatusFilters.HAS_ATTACHMENT,
        options: [
          { value: true, label: HasAttachmentMessage },
          { value: false, label: NoAttachmentMessage },
        ],
      },
      ...approveConfig,
      {
        name: StatusFilters.HAS_EXPENSE_TYPE,
        options: [{ value: false, label: UncategorizedStatusMessage }],
      },
      {
        name: StatusFilters.IS_FILED,
        options: [
          { value: true, label: FiledStatusMessage },
          { value: false, label: NotFiledStatusMessage },
        ],
      },
      {
        name: StatusFilters.IS_PROCESSED,
        onChangeOverWriteRules: {
          [StatusFilters.IS_ACCOUNTING_EXPORTED]: undefined,
        },
        options: [
          { value: false, label: AccountingUnprocessedStatusMessage },
          { value: true, label: AccountingProcessedStatusMessage },
        ],
      },
      {
        name: StatusFilters.IS_ACCOUNTING_EXPORTED,
        onChangeOverWriteRules: { [StatusFilters.IS_PROCESSED]: undefined },
        options: [{ value: true, label: AccountingAdvancedExportedStatusMessage }],
      },
      {
        name: StatusFilters.HAS_ACCOUNTING_TODO,
        options: [
          { value: true, label: HasAccountingTodoStatusMessage },
          { value: false, label: NoAccountingTodoStatusMessage },
        ],
      },
      {
        name: StatusFilters.HAS_UNFINISHED_ACCOUNTING_TODO,
        options: [{ value: true, label: HasUnfinishedAccountingTodoStatusMessage }],
      },
      {
        name: StatusFilters.IS_ACCOUNTING_TODO_DONE,
        options: [{ value: true, label: AccountingTodoDoneStatusMessage }],
      },
      {
        name: StatusFilters.IS_EXCLUDED_FROM_ACCOUNTING,
        options: [{ value: true, label: ExcludedFromAccountingStatusMessage }],
      },
    ]

    if (hasIntegrationSzamlazz) {
      config.splice(3, 0, {
        name: StatusFilters.IS_READY_TO_SYNC_SZAMLAZZ,
        options: [{ value: true, label: ReaydToSyncStatusMessage }],
      })
    }

    if (isAccountingIdEnabled) {
      config.push({
        name: StatusFilters.HAS_ACCOUNTING_ID,
        options: [
          { value: true, label: HasAccountingIdStatusMessage },
          { value: false, label: NoAccountingIdStatusMessage },
        ],
      })
    }

    if (isVatPeriodEnabled) {
      config.push({
        name: StatusFilters.HAS_VAT_PERIOD,
        options: [
          { value: true, label: HasVatPeriodStatusMessage },
          { value: false, label: NoVatPeriodStatusMessage },
        ],
      })
    }

    return config
  }

  const config: StatusFilterConfig[] = [
    {
      name: StatusFilters.IS_NEW,
      options: [{ value: true, label: NewStatusMessage }],
    },
    {
      name: StatusFilters.IS_UPDATED,
      options: [{ value: true, label: UpdatedStatusMessage }],
    },
    {
      name: StatusFilters.HAS_ARTIFACT,
      options: [
        { value: true, label: HasArtifactStatusMessage },
        { value: false, label: NoArtifactStatusMessage },
      ],
    },
    {
      name: StatusFilters.IS_PROCESSED,
      onChangeOverWriteRules: {
        [StatusFilters.IS_ACCOUNTING_EXPORTED]: undefined,
      },
      options: [
        { value: false, label: AccountingUnprocessedStatusMessage },
        { value: true, label: AccountingProcessedStatusMessage },
      ],
    },
    {
      name: StatusFilters.IS_ACCOUNTING_EXPORTED,
      onChangeOverWriteRules: { [StatusFilters.IS_PROCESSED]: undefined },
      options: [{ value: true, label: AccountingExpressExportedStatusMessage }],
    },
    {
      name: StatusFilters.HAS_POST_IT,
      options: [
        { value: true, label: HasPostItMessage },
        { value: false, label: NoPostItMessage },
      ],
    },
    {
      name: StatusFilters.HAS_ATTACHMENT,
      options: [
        { value: true, label: HasAttachmentMessage },
        { value: false, label: NoAttachmentMessage },
      ],
    },
    ...approveConfig,
    {
      name: StatusFilters.HAS_EXPENSE_TYPE,
      options: [{ value: false, label: UncategorizedStatusMessage }],
    },
    {
      name: StatusFilters.IS_FILED,
      options: [
        { value: true, label: FiledStatusMessage },
        { value: false, label: NotFiledStatusMessage },
      ],
    },
  ]

  if (hasIntegrationSzamlazz) {
    config.splice(5, 0, {
      name: StatusFilters.IS_READY_TO_SYNC_SZAMLAZZ,
      options: [{ value: true, label: ReaydToSyncStatusMessage }],
    })
  }

  return config
}
